"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buttonClasses = buttonClasses;
exports.Button = Button;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const cn_1 = require("../lib/cn");
const tooltip_1 = require("./tooltip");
/**
 * ⚠️ **Every class string here is transcribed from the mockup, not approximated.** Where it
 * states a value — `h-8`, `ring-[3px]`, `text-[13px]` — that value is the design, and
 * rounding it produces a control that is nearly right in a way nobody can name.
 */
/**
 * ⚠️⚠️ **`font-semibold`, AND `font-medium` IS WHAT THE PRODUCT OWNER CALLED *"not thick"*.**
 * Every button in the mockup reads heavier than ours did at the same size, and the difference is
 * the weight rather than the face — one family sets everything here (CLAUDE.md §3). Changed on the
 * BASE so it reaches every variant and every size at once, including the header CTAs, which the
 * ruling exempted from the SIZE change but not from this one.
 */
/**
 * ⚠️ **A press scales to 0.97**, so a click is felt before its result arrives. Tailwind 4's
 * `scale-*` writes the `scale` property rather than `transform`, which is why the transition names
 * `scale` — `transition-transform` alone would let the press snap. Never on a disabled button,
 * which has nothing to acknowledge.
 */
const BASE = 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold ' +
    'transition-[color,background-color,border-color,box-shadow,scale] duration-150 ease-out ' +
    'active:not-disabled:scale-[0.97] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 ' +
    'focus-visible:border-ring disabled:pointer-events-none disabled:opacity-50 shrink-0';
const VARIANTS = {
    default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
    /* ⚠️ `text-destructive-foreground`, and it USED to be `text-white` — with a comment here
     * arguing that the palette declared no such token and that inventing one would mean choosing a
     * value for eight themes. The eight-theme package declared it, per theme, because the argument
     * had it backwards: white on the destructive fill measures under 2.2:1 in the four pastel dark
     * palettes, so `text-white` was already choosing a value for eight themes — badly, in six. */
    destructive: 'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
    /**
     * ⚠⚠️ **`text-foreground` ON THE TWO VARIANTS THAT SET NO COLOUR — AND ITS ABSENCE MADE THE
     * WHOLE SIDEBAR PURPLE.** `default`, `destructive` and `secondary` each name their own text
     * colour; `ghost` and `outline` used to name none and rely on inheritance. That is correct for a
     * `<button>`, which inherits `--foreground` from `body`.
     *
     * It is wrong for an `<a>`. `buttonClasses` is exported so that **a control which navigates can be
     * an anchor and still look like a button** (see the note below it), and the base stylesheet paints
     * every anchor `a { color: var(--primary) }`. A type selector loses to a utility class, so the
     * ACTIVE row — `secondary` — looked right while every inactive row rendered violet. The mockup's
     * own sidebar is `Shadcn.Button variant="ghost"`, i.e. `<button>`, which is why it never showed
     * there.
     *
     * ⚠️ **A no-op for every existing `<button>`**, which already resolved to this colour by
     * inheritance — and callers still win, because `className` is merged last.
     */
    outline: 'border bg-background text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground',
    secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
    ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
    /* ⚠️ No hover underline — item 69 removed them app-wide; the colour carries the link. */
    link: 'text-primary underline-offset-4',
};
/**
 * ⚠️ **Every height went up one step — 32→36 default, 28→32 small.** *"The buttons on the mockup
 * are proper size, ours are small."* The mockup's own dialog footers and section actions sit at 36
 * and 32; ours were a control's worth smaller everywhere, which is what made a form's Save read as
 * a secondary control.
 *
 * ⚠️ **`icon-sm` stays 28px**, because it is the glyph beside a row's text — growing it would move
 * every list row's height for a control that is not the point of the row.
 */
const SIZES = {
    default: 'h-9 px-3.5 has-[>svg]:px-3',
    sm: 'h-8 rounded-md gap-1.5 px-3 text-[13px] has-[>svg]:px-2.5',
    lg: 'h-10 rounded-md px-4',
    icon: 'size-9',
    'icon-sm': 'size-7',
};
/**
 * ⚠️ **`type="button"` by default.** A `<button>` inside a form submits it otherwise, which
 * is the sort of defect that only appears once there is a form — and then looks like a
 * routing bug.
 *
 * ⚠️ **A control that NAVIGATES is not a button.** Render an `<a>` for that, or wrap this in
 * one — never a `<button>` around an `<a>`, which is `nested-interactive` and a WCAG failure.
 * The outgoing project shipped exactly that on a list row. An anchor is also what survives a
 * middle-click, and what a reachability check can see.
 */
/**
 * The button's classes, without the button.
 *
 * ⚠️⚠️ **EXPORTED SO THAT A LINK CAN LOOK LIKE A BUTTON WITHOUT BEING ONE.** The note above is
 * explicit that a control which navigates has to be an `<a>` — but until this existed, honouring
 * that meant either hand-copying the class string (which then drifts from the variants) or wrapping
 * a `<button>` around an `<a>`, which is `nested-interactive` and the exact WCAG failure the
 * outgoing project shipped on a list row. One helper, one skin, either element.
 */
function buttonClasses(variant = 'default', size = 'default', className) {
    return (0, cn_1.cn)(BASE, VARIANTS[variant], SIZES[size], className);
}
/**
 * ⚠️⚠️ **`title` IS THE KIT'S TOOLTIP, NEVER THE BROWSER'S** (20 Oct 2026 — no native tooltips,
 * anywhere, ever). It is taken out of the spread and shown through `useTooltip`, on the button
 * itself rather than on a wrapper, so a `w-full` or `flex-1` button keeps its size.
 *
 * ⚠️ **An icon-only button named only by its `title` keeps that name.** The native attribute was
 * the accessible name of last resort; without it such a button would announce as nothing. So when
 * the caller set no `aria-label`/`aria-labelledby` and the content holds no text, the title becomes
 * the `aria-label`.
 */
function Button({ variant = 'default', size = 'default', className, children, type = 'button', title, ...rest }) {
    const tip = (0, tooltip_1.useTooltip)(title);
    const named = rest['aria-label'] !== undefined || rest['aria-labelledby'] !== undefined || hasText(children);
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("button", { type: type, className: buttonClasses(variant, size, className), "data-size": size, ...rest, ...tip.triggerProps(rest), "aria-label": !named && title !== undefined && title !== '' ? title : rest['aria-label'], children: children }), tip.bubble] }));
}
/** Whether a subtree renders any text — the test for "this button already has a name". */
function hasText(node) {
    if (typeof node === 'string')
        return node.trim() !== '';
    if (typeof node === 'number')
        return true;
    if (Array.isArray(node))
        return node.some((child) => hasText(child));
    if ((0, react_1.isValidElement)(node))
        return hasText(node.props.children);
    return false;
}
//# sourceMappingURL=button.js.map